const EXPERIENCE=Object.freeze([
  Object.freeze({
    key:'beginner',
    labels:Object.freeze({pl:'Początkujący',en:'Beginner'}),
    descriptions:Object.freeze({
      pl:'Dopiero zaczynam lub potrzebuję prowadzenia krok po kroku.',
      en:'I am just getting started or want step-by-step guidance.'
    })
  }),
  Object.freeze({
    key:'intermediate',
    labels:Object.freeze({pl:'Średniozaawansowany',en:'Intermediate'}),
    descriptions:Object.freeze({
      pl:'Mam już doświadczenie i chcę większej powtarzalności oraz kontroli.',
      en:'I already have experience and want more repeatability and control.'
    })
  }),
  Object.freeze({
    key:'advanced',
    labels:Object.freeze({pl:'Zaawansowany',en:'Advanced'}),
    descriptions:Object.freeze({
      pl:'Samodzielnie dobieram proces i chcę głównie danych, narzędzi i porównań.',
      en:'I design the process myself and mainly want data, tools and comparisons.'
    })
  })
]);

const GOALS=Object.freeze([
  ['learn_basics','Nauczyć się podstaw','Learn the basics'],
  ['repeatability','Poprawić powtarzalność','Improve repeatability'],
  ['better_flavour','Poprawić smak i aromat','Improve flavour and aroma'],
  ['cold_smoking','Rozwinąć wędzenie na zimno','Develop cold smoking'],
  ['preservation','Lepsze planowanie i utrwalanie','Improve planning and preservation'],
  ['experiment','Eksperymentować i porównywać','Experiment and compare'],
  ['other','Inny cel','Other goal']
].map(([key,pl,en])=>Object.freeze({key,labels:Object.freeze({pl,en})})));

const PRODUCTS=Object.freeze([
  ['pork','Wieprzowina','Pork'],
  ['beef','Wołowina','Beef'],
  ['poultry','Drób','Poultry'],
  ['fish','Ryby','Fish'],
  ['sausages','Kiełbasy i wędliny','Sausages and cured meats'],
  ['cheese','Sery','Cheese'],
  ['vegetables','Warzywa','Vegetables'],
  ['other','Inne','Other']
].map(([key,pl,en])=>Object.freeze({key,labels:Object.freeze({pl,en})})));

function localeKey(locale){
  return String(locale||'pl').trim().toLowerCase().startsWith('en')?'en':'pl';
}

function optionList(catalogue,locale,{withDescription=false}={}){
  const lang=localeKey(locale);
  return Object.freeze(catalogue.map(item=>Object.freeze({
    key:item.key,
    label:item.labels[lang]||item.labels.pl,
    ...(withDescription?{description:item.descriptions[lang]||item.descriptions.pl}:{})
  })));
}

function catalogueKeys(catalogue){
  return new Set(catalogue.map(item=>item.key));
}

function safeUnknownKey(value){
  const key=String(value||'').trim().toLowerCase();
  return /^[a-z][a-z0-9_]{1,63}$/.test(key)?key:null;
}

function selection(values,catalogue,{allowUnknown=true}={}){
  if(values==null)return Object.freeze([]);
  if(!Array.isArray(values))throw new TypeError('Selection must be an array');
  const known=catalogueKeys(catalogue);
  const seen=new Set();
  const result=[];
  for(const raw of values){
    const key=safeUnknownKey(raw);
    if(!key)throw new TypeError('Invalid onboarding option');
    if(!known.has(key)&&!allowUnknown)throw new TypeError(`Unsupported onboarding option: ${key}`);
    if(!seen.has(key)){seen.add(key);result.push(key);}
  }
  return Object.freeze(result);
}

function customText(value,{requiredWhenOther=false,selected=[]}={}){
  const text=String(value??'').trim();
  if(text.length>160)throw new TypeError('Custom onboarding value is too long');
  if(requiredWhenOther&&selected.includes('other')&&!text){
    throw new TypeError('Custom onboarding value is required when other is selected');
  }
  return text||null;
}

export function experienceOptions({locale='pl'}={}){
  return optionList(EXPERIENCE,locale,{withDescription:true});
}

export function smokingGoalOptions({locale='pl'}={}){
  return optionList(GOALS,locale);
}

export function productPreferenceOptions({locale='pl'}={}){
  return optionList(PRODUCTS,locale);
}

export function normaliseExperienceSelection(value){
  if(value==null||value==='')return null;
  const key=String(value).trim().toLowerCase();
  if(!catalogueKeys(EXPERIENCE).has(key))throw new TypeError('Unsupported experience level');
  return key;
}

export function buildSmokingGoalSelection(values,{otherText=null}={}){
  const selected=selection(values,GOALS,{allowUnknown:false});
  return Object.freeze({
    values:selected,
    otherText:customText(otherText,{requiredWhenOther:true,selected})
  });
}

export function buildProductPreferenceSelection(values,{otherText=null}={}){
  const selected=selection(values,PRODUCTS,{allowUnknown:true});
  return Object.freeze({
    values:selected,
    otherText:customText(otherText,{requiredWhenOther:false,selected})
  });
}

export function editSmokingGoalState(profile={}){
  const selected=selection(profile.smokingGoals||[],GOALS,{allowUnknown:true});
  const known=catalogueKeys(GOALS);
  return Object.freeze({
    selected:Object.freeze(selected.filter(key=>known.has(key))),
    unknown:Object.freeze(selected.filter(key=>!known.has(key))),
    otherText:customText(profile.customSmokingGoal)
  });
}

export function editProductPreferenceState(profile={}){
  const selected=selection(profile.productPreferences||[],PRODUCTS,{allowUnknown:true});
  const known=catalogueKeys(PRODUCTS);
  return Object.freeze({
    selected:Object.freeze(selected.filter(key=>known.has(key))),
    unknown:Object.freeze(selected.filter(key=>!known.has(key))),
    otherText:customText(profile.customProductPreference)
  });
}

export function onboardingProfilePatch({
  experienceLevel=null,
  smokingGoals=[],
  customSmokingGoal=null,
  productPreferences=[],
  customProductPreference=null
}={}){
  const goals=buildSmokingGoalSelection(smokingGoals,{otherText:customSmokingGoal});
  const products=buildProductPreferenceSelection(productPreferences,{otherText:customProductPreference});
  return Object.freeze({
    experienceLevel:normaliseExperienceSelection(experienceLevel),
    smokingGoals:goals.values,
    customSmokingGoal:goals.otherText,
    productPreferences:products.values,
    customProductPreference:products.otherText
  });
}
